import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'reactstrap';
import { useLocation } from "react-router-dom";

import Analytics from '../../../analytics/Analytics';
import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import RouteService from '../../../services/RouteService';
import AccountStore from '../../../store/AccountStore';
import AdStore from '../../../store/AdStore';
import CategoryStore from '../../../store/CategoryStore';
import ConfigStore from '../../../store/ConfigStore';
import CustomNavigationLinkStore from '../../../store/CustomNavigationLinkStore';
import MenuStore from '../../../store/MenuStore';
import SectionStore from '../../../store/SectionStore';
import UIStore from '../../../store/UIStore';
import AccountModalTab from '../../../types/AccountModalTab';
import AdZones from '../../../types/AdZones';
import CustomNavigationLinkParentType from '../../../types/CustomNavigationLinkParentType';
import LinkType from '../../../types/LinkType';
import MobileNavigationTab from '../../../types/MobileNavigationTab';
import Paths from '../../../types/Paths';
import ClientLoginLink from '../../account/ClientLoginLink';
import ContentSlot from '../../ad/ContentSlot';
import CategoryBadges from '../../common/CategoryBadges';
import Icon from '../../common/Icon';
import MobileNavigationRegionalSettingsSummary from '../../common/MobileNavigationRegionalSettingsSummary';
import MobileNavigationList from '../../navigation/MobileNavigationList';
import useMobileNavigation from './hook';
import useNavigationEntityConverter from "../../../hooks/navigation/useNavigationEntityConverter";
import MenuPosition from "../../../types/MenuPosition";
import CheckoutStore from "../../../store/CheckoutStore";

const MobileNavigation = ({
  accountStore,
  adStore,
  categoryStore,
  checkoutStore,
  configStore,
  customNavigationLinkStore,
  menuStore,
  sectionStore,
  uiStore,
  routeService,
  analytics,
  navigation,
  shouldHideProductContent,
  onTabChange,
  onClose,
  menuPosition,
}) => {
  const mobileNavigationRef = useRef(null);
  const location = useLocation();

  useMobileNavigation({
    navigation,
    mobileNavigationRef,
    onClose,
    onTabChange,
  });

  const getFooter = () => {
    return (
      <div className="MobileNavigation__footer">
        {uiStore.isMobile && getBanners()}
        <MobileNavigationRegionalSettingsSummary />
      </div>
    );
  };

  const getBanners = () => {
    const ads = adStore.getGlobalAdsByAdZone(AdZones.HEADER_CONTENT);
    if (!ads || ads.length === 0) {
      return null;
    }

    return (
      <div className="MobileNavigation__content-slots">
        {ads.map((ad) => (
          <ContentSlot
            ad={ad}
            bannerZone={AdZones.HEADER_CONTENT}
            key={ad.id}
          />
        ))}
      </div>
    );
  };

  const toggleTab = (tabId) => {
    navigation.selectTab(tabId);
  };

  if (configStore.account.isMoneyTransfer) {
    toggleTab(MobileNavigationTab.ACCOUNT);
  }

  const getProductTabItems = useCallback(() => {
    const staticItems = [];

    if (accountStore.isRetailer) {
      staticItems.push({
        id: 'CustomerProducts',
        label: (
          <FormattedMessage {...globalTranslations.customerProductsTitle} />
        ),
        path: routeService.getPath(Paths.CustomerProducts),
      });
    }

    if (configStore.navigation.sidebar.showOnSale) {
      staticItems.push({
        id: 'OnSale',
        label: <FormattedMessage {...globalTranslations.onSaleTitle} />,
        path: routeService.getPath(Paths.OnSale),
      });
    }

    if (configStore.navigation.sidebar.showNewProducts) {
      staticItems.push({
        id: 'NewProducts',
        label: <FormattedMessage {...globalTranslations.newProductsTitle} />,
        path: routeService.getPath(Paths.NewProducts),
      });
    }

    if (configStore.navigation.sidebar.showSoldProducts) {
      staticItems.push({
        id: 'SoldProducts',
        label: <FormattedMessage {...globalTranslations.soldProductsTitle} />,
        path: routeService.getPath(Paths.SoldProducts),
      });
    }

    if (configStore.navigation.sidebar.showMostSold) {
      staticItems.push({
        id: 'MostSold',
        label: <FormattedMessage {...globalTranslations.mostPopularTitle} />,
        path: routeService.getPath(Paths.PopularProducts),
      });
    }

    if (configStore.navigation.sidebar.showRecommended) {
      staticItems.push({
        id: 'Recommended',
        label: <FormattedMessage {...globalTranslations.weRecommendTitle} />,
        path: routeService.getPath(Paths.RecommendedProducts),
      });
    }

    staticItems.push(getSpacerEntity());

    staticItems.push({
      id: 'info',
      onClick: () => {
        toggleTab(MobileNavigationTab.INFO);
      },
      label: (
        <span className="MobileNavigationList__sublabel">
          <FormattedMessage {...globalTranslations.infoTitle} />
          <Icon name="info-circle" />
        </span>
      ),
    });

    if (!configStore.siteConfig.isShoppingCenter) {
      staticItems.push({
        id: 'my-account',
        onClick: () => {
          toggleTab(MobileNavigationTab.ACCOUNT);
        },
        label: (
          <span className="MobileNavigationList__sublabel">
            <FormattedMessage {...globalTranslations.myAccountTitle} />
            <Icon name="user" />
          </span>
        ),
      });
    }

    staticItems.push(getSpacerEntity());

    let sectionsOrTopLevelCategories;

    if (configStore.activateSections) {
      sectionsOrTopLevelCategories = sectionStore.activeSections.map(
        convertSectionToEntity
      );
    } else {
      sectionsOrTopLevelCategories = categoryStore.categories.map((category) =>
        convertCategoryToEntity(category, null)
      );
    }

    return sectionsOrTopLevelCategories.concat(staticItems);
  }, [sectionStore.activeSection])

  // TODO: Convert to use navigation entity converter
  const convertSectionToEntity = (section) => {
    return {
      id: section.id,
      label: section.navigationName,
      path: routeService.getPath(section.path),
      active: sectionStore.activeSection === section,
      children: section.mainCategories.map((category) =>
        convertCategoryToEntity(category, section)
      ),
      customNavLinks: getCustomNavigationLinks(
        section,
        CustomNavigationLinkParentType.SECTION
      ),
    };
  };

  const convertCategoryToEntity = (category, section) => {
    const children = section
      ? category.getNonBlockedChildren(section.blockedCategoriesDict)
      : category.children;
    return {
      id: category.id,
      label: (
        <span>
          {category.navigationName} <CategoryBadges category={category} />
        </span>
      ),
      path: routeService.getPath(category.path, section),
      active: categoryStore.isInActiveCategoryHierarchy(category),
      children: children.map((subcategory) =>
        convertCategoryToEntity(subcategory, section)
      ),
      customNavLinks: getCustomNavigationLinks(
        category,
        CustomNavigationLinkParentType.CATEGORY
      ),
    };
  };

  const getCustomNavigationLinks = (categoryOrSection, type) => {
    if (type === CustomNavigationLinkParentType.CATEGORY) {
      return convertCustomNavigationLinksToEntityItems(
        customNavigationLinkStore.filterByCategoryId(categoryOrSection.id)
      );
    }

    if (type === CustomNavigationLinkParentType.SECTION) {
      return convertCustomNavigationLinksToEntityItems(
        customNavigationLinkStore.filterBySectionId(categoryOrSection.id)
      );
    }
  };

  const convertCustomNavigationLinksToEntityItems = (
    topLevelCustomNavigationLinks
  ) => {
    return topLevelCustomNavigationLinks.map((customNavigationLink) => {
      return {
        id: customNavigationLink.id,
        label: customNavigationLink.name,
        path: getCategoryListPath(customNavigationLink),
        location: customNavigationLink.location,
        parent_sibling_id: customNavigationLink,
        customNavLinks: convertCustomNavigationLinksToEntityItems(
          customNavigationLinkStore.findChildLinks(customNavigationLink.id)
        ),
      };
    });
  };

  const getCategoryListPath = (customNavLink) => {
    if (customNavLink.link_type === LinkType.EXTERNAL_LINK) {
      return customNavLink.link;
    }

    if (customNavLink.link_type === LinkType.INTERNAL_LINK) {
      return routeService.getPath(customNavLink.link);
    }

    return routeService.getPath(customNavLink.link, sectionStore.activeSection);
  };

  const getPath = () => {
    const frontPage = routeService.getPath(Paths.FrontPage);
    const pathIsSelf = uiStore.lastPathname === location.pathname;

    if (pathIsSelf) {
      return frontPage;
    } else {
      return uiStore.lastPathname || frontPage;
    }
  };

  const getInfoTabItems = useCallback(() => {
    const staticItems = [getSpacerEntity()];

    configStore.customerService.email.length > 0 &&
      staticItems.push({
        id: 'email',
        path: `mailto:${configStore.customerService.email}`,
        label: (
          <span className="MobileNavigationList__sublabel">
            <FormattedMessage {...globalTranslations.infoSendEmail} />
            <Icon name="envelope-square" />
          </span>
        ),
      });

    configStore.customerService.phone.length > 0 &&
      staticItems.push({
        id: 'phone',
        path: `tel:${configStore.customerService.phone}`,
        label: (
          <span className="MobileNavigationList__sublabel">
            <div className="MobileNavigationList__text-container d-flex flex-column">
              <FormattedMessage
                {...globalTranslations.infoCallPhone}
                values={{ phone: configStore.customerService.phone }}
              />
              <FormattedMessage
                {...globalTranslations.isOpen}
                values={{
                  timespan: configStore.customerService.open,
                }}
              />
            </div>
            <span className="MobileNavigationList__sublabel-placer">
              <span className="fa-stack fa-stack-small">
                <Icon name="square" className="fa-stack-1x" />
                <Icon name="phone" className="fa-stack-3qx fa-inverse" />
              </span>
            </span>
          </span>
        ),
      });

    const items = useNavigationEntityConverter({
      entities: menuPosition === MenuPosition.CHECKOUT
        ? menuStore.checkoutMenus
        : menuStore.menus
    })
      .concat(staticItems);

    if (menuPosition === MenuPosition.CHECKOUT) {
      const path = getPath();

      if (menuStore.checkoutMenus.length > 0) {
        items.unshift(getSpacerEntity());
      }

      items.unshift({
        id: '_top',
        path,
        type: LinkType.INTERNAL_LINK,
        disabled: checkoutStore.checkoutIsLoading,
        image: null,
        label: (
          <span>
            <Icon name="angle-left" />
            <FormattedMessage {...globalTranslations.backToStore} />
          </span>
        ),
      });
    }

    return items;
  }, [menuStore.menus, checkoutStore.checkoutIsLoading, uiStore.lastPathname, menuStore.checkoutMenus]);

  const getSpacerEntity = () => {
    return {
      id: 'Spacer' + uniqueId(),
      label: '',
    };
  };

  const getAccountTabItems = useCallback(() => {
    const { accountModal } = accountStore;
    const tabItems = [];

    if (accountStore.loggedIn) {
      return getLoggedInTabItems();
    } else {
      if (
        (!configStore.account.disableLogin &&
          configStore.siteConfig.isWebStore) ||
        configStore.account.isMoneyTransfer
      ) {
        tabItems.push({
          id: 'login',
          onClick: () => {
            accountModal.setTab(AccountModalTab.LOGIN);
            accountModal.toggle();
          },
          label: (
            <span className="MobileNavigationList__sublabel">
              <FormattedMessage {...globalTranslations.logInSentence} />
              <Icon name="sign-in" />
            </span>
          ),
        });
      }
      if (
        configStore.registration.enabled &&
        (configStore.siteConfig.isWebStore ||
          configStore.account.isMoneyTransfer)
      ) {
        tabItems.push({
          id: 'register',
          onClick: () => {
            accountModal.setTab(AccountModalTab.REGISTER);
            accountModal.toggle();
          },
          label: (
            <span className="MobileNavigationList__sublabel">
              <FormattedMessage {...globalTranslations.registerTitle} />
            </span>
          ),
        });
      }
      if (
        configStore.wishlist.enabled &&
        !configStore.account.isMoneyTransfer
      ) {
        tabItems.push({
          id: 'wishlist',
          path: routeService.getPath(Paths.WishList),
          label: <FormattedMessage {...globalTranslations.wishListTitle} />,
        });
      }

      return tabItems;
    }
  }, []);

  const getLoggedInTabItems = () => {
    let accountPath = accountStore.isBalanceBuyer
      ? Paths.MyAccountBalanceUser
      : Paths.MyAccount;
    if (accountStore.isBalanceUser) {
      accountPath = Paths.MyAccountBalanceSubUser;
    }

    const tabItems = [];

    if (configStore.account.enableMyAccount) {
      tabItems.push({
        id: 'customerAccount',
        path: routeService.getPath(accountPath),
        label: (
          <FormattedMessage {...globalTranslations.myCustomerAccountTitle} />
        ),
      });
    }

    if (!accountStore.isBalanceUser) {
      if (configStore.account.enableMyAccount) {
        const accountType = accountStore.isBalanceBuyer
          ? getBalanceUserAccount()
          : getCustomerUserOrderHistory();
        tabItems.push(accountType);
      }

      if (configStore.wishlist.enabled) {
        tabItems.push({
          id: 'wishlist',
          path: routeService.getPath(Paths.WishList),
          label: <FormattedMessage {...globalTranslations.wishListTitle} />,
        });
      }
    }

    if (accountStore.isClientLoginEnabled) {
      const label = (
        <>
          <ClientLoginLink />
          {accountStore.isClient && (
            <span className="MobileNavigation--client-login">
              ({accountStore.name})
            </span>
          )}
        </>
      );
      tabItems.push({
        id: 'clientLogin',
        onClick: () => {
          accountStore.accountModal.setTab(AccountModalTab.CHANGE_CLIENT);
          accountStore.accountModal.toggle();
        },
        label: label,
      });
    }

    tabItems.push({
      id: 'logout',
      onClick: () => {
        analytics.clearCart();
        accountStore.logout();
      },
      label: (
        <span className="MobileNavigationList__sublabel">
          <FormattedMessage {...globalTranslations.accountLogOutSentence} />
          <Icon name="sign-out" />
        </span>
      ),
    });

    return tabItems;
  };

  const getBalanceUserAccount = () => {
    return {
      id: 'balanceUsers',
      path: routeService.getPath(Paths.MyAccountBalanceUserPaymentHistory),
      label: (
        <FormattedMessage
          {...globalTranslations.balanceUserAccountBalanceInfo}
        />
      ),
    };
  };

  const getCustomerUserOrderHistory = () => {
    return {
      id: 'orderHistory',
      path: routeService.getPath(Paths.MyAccountOrderList),
      label: <FormattedMessage {...globalTranslations.myOrderHistoryTitle} />,
    };
  };

  const tree = useMemo(() => {
    const accountTabItems = getAccountTabItems();

    const treeData = [];

    if (!shouldHideProductContent) {
      treeData.push({
        id: MobileNavigationTab.PRODUCTS,
        label: <FormattedMessage {...globalTranslations.productsTitle} />,
        children: getProductTabItems(),
      });
    }

    treeData.push({
      id: MobileNavigationTab.INFO,
      label: <FormattedMessage {...globalTranslations.infoTitle} />,
      children: getInfoTabItems(),
    });

    if (
      accountTabItems.length > 0 &&
      !configStore.siteConfig.isShoppingCenter
    ) {
      treeData.push({
        id: MobileNavigationTab.ACCOUNT,
        label: <FormattedMessage {...globalTranslations.myAccountTitle} />,
        children: accountTabItems,
      });
    }

    return treeData;
  }, [getAccountTabItems, getInfoTabItems, getProductTabItems]);

  return (
    <div className="MobileNavigation" ref={mobileNavigationRef}>
      <div className="MobileNavigation__tabs">
        {tree.map((tab) => (
          <Button
            key={tab.id}
            className={classNames('MobileNavigation__tab', {
              'MobileNavigation__tab--active': tab.id === navigation.activeTab,
            })}
            color="plain"
            onClick={() => toggleTab(tab.id)}
          >
            {tab.label}
          </Button>
        ))}
      </div>
      <MobileNavigationList
        activeTabId={navigation.activeTab}
        entities={tree}
        close={navigation.close}
        hasSections={!!configStore.activateSections}
      />
      {getFooter()}
    </div>
  );
};

MobileNavigation.propTypes = {
  adStore: modelOf(AdStore).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  categoryStore: modelOf(CategoryStore).isRequired,
  checkoutStore: modelOf(CheckoutStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  customNavigationLinkStore: modelOf(CustomNavigationLinkStore).isRequired,
  menuStore: modelOf(MenuStore).isRequired,
  sectionStore: modelOf(SectionStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  shouldHideProductContent: PropTypes.bool,
  onTabChange: PropTypes.func,
  onClose: PropTypes.func,
  navigation: PropTypes.object.isRequired,
  menuPosition: PropTypes.string
};

export default inject(
  'adStore',
  'accountStore',
  'categoryStore',
  'checkoutStore',
  'configStore',
  'customNavigationLinkStore',
  'menuStore',
  'sectionStore',
  'uiStore',
  'analytics',
  'routeService'
)(observer(MobileNavigation));
